import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ZoomableImage from "@/components/site/ZoomableImage";

/**
 * Shared in-page watch lightbox used by both the Allocation page and the
 * Catalog pages. Keeps the visitor on the page they came from.
 *
 * Props:
 *  - watch        the watch object from watchImages.js (slug, name, ref, metal, notes, front, back, line)
 *  - onClose      handler that clears the open watch
 *  - catalogHref  optional deep-link "/collection/<route>#<slug>" if provided, renders
 *                 an "Open in catalog →" anchor at the foot of the modal
 */
export default function WatchPreviewModal({ watch, onClose, catalogHref }) {
  const [showingBack, setShowingBack] = useState(false);

  useEffect(() => {
    if (!watch) return undefined;
    setShowingBack(false);
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [watch, onClose]);

  const activeSrc = showingBack && watch?.back ? watch.back : watch?.front;

  return (
    <AnimatePresence>
      {watch && (
        <motion.div
          key={watch.slug}
          data-testid="watch-preview-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`${watch.name} preview`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-10"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close preview"
            onClick={onClose}
            className="absolute inset-0 bg-[#0C0F0A]/85 backdrop-blur-xl border-0 p-0 cursor-default"
            data-testid="watch-preview-backdrop"
          />

          {/* Card */}
          <motion.figure
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 12 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="relative w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 bg-[#0C0F0A] border border-[#C5A059]/40 max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image side */}
            <div className="relative aspect-square w-full bg-gradient-to-br from-[#161B14] via-[#0C0F0A] to-[#0C0F0A]">
              <ZoomableImage
                src={activeSrc}
                alt={`CLEMENT — ${watch.name}${showingBack ? " caseback" : ""}`}
                fallbackTitle={watch.name}
                fallbackSub={watch.ref}
                testId="watch-preview-zoomable"
              />
              {watch.back && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-px bg-[#0C0F0A]/85 border border-[#C5A059]/40">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowingBack(false);
                    }}
                    data-testid="watch-preview-front-btn"
                    className={`px-4 py-2 text-[10px] uppercase tracking-[0.34em] transition-colors ${
                      !showingBack
                        ? "bg-[#C5A059] text-[#0C0F0A]"
                        : "text-[#A8A9AD] hover:text-[#F5F2EB]"
                    }`}
                  >
                    Front
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowingBack(true);
                    }}
                    data-testid="watch-preview-back-btn"
                    className={`px-4 py-2 text-[10px] uppercase tracking-[0.34em] transition-colors ${
                      showingBack
                        ? "bg-[#C5A059] text-[#0C0F0A]"
                        : "text-[#A8A9AD] hover:text-[#F5F2EB]"
                    }`}
                  >
                    Caseback
                  </button>
                </div>
              )}
            </div>

            {/* Detail side */}
            <figcaption className="p-8 sm:p-12 flex flex-col">
              <div className="flex items-center gap-3">
                <span className="block w-8 h-px bg-[#C5A059]" />
                <span className="text-[10px] uppercase tracking-[0.42em] text-[#C5A059]">
                  {watch.line || "Reference"}
                </span>
              </div>

              <h3 className="mt-6 font-serif-display text-3xl sm:text-4xl text-[#F5F2EB] font-light leading-tight tracking-tight">
                {watch.name}
              </h3>
              {watch.ref && (
                <div className="mt-2 text-[10px] uppercase tracking-[0.42em] text-[#A8A9AD]">
                  {watch.ref}
                </div>
              )}

              {watch.metal && (
                <div className="mt-6 text-[11px] uppercase tracking-[0.32em] text-[#C5A059]">
                  {watch.metal}
                </div>
              )}
              {watch.notes && (
                <p className="mt-6 text-[#d6d3c6] text-[14px] sm:text-[15px] leading-relaxed">
                  {watch.notes}
                </p>
              )}

              <div className="hairline mt-8" />

              <div className="mt-auto pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-[10px] uppercase tracking-[0.42em] text-[#A8A9AD]/60">
                  Click image to zoom · Esc to close
                </p>
                <div className="flex items-center gap-6">
                  {catalogHref && (
                    <Link
                      to={catalogHref}
                      onClick={onClose}
                      data-testid="watch-preview-open-catalog"
                      className="text-[10px] uppercase tracking-[0.42em] text-[#C5A059] hover:text-[#F5F2EB] transition-colors"
                    >
                      Open in catalog →
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={onClose}
                    data-testid="watch-preview-close"
                    className="text-[10px] uppercase tracking-[0.42em] text-[#A8A9AD] hover:text-[#F5F2EB] transition-colors"
                  >
                    Close ×
                  </button>
                </div>
              </div>
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
